/**
 * The notifications channel: the socket at /ws, and the fallback when there
 * is none (docs/notifications/implementation-guide.md 7.2, 7.3).
 *
 * Pure - the clock, the timers, the socket and the API are injected - so
 * channel.test.js runs every rule in node, as businessDay/dayPoller.js's are.
 *
 * THE RULES
 *
 *   - Connect with a one-time ticket (POST notifications/socket-ticket), then
 *     wss://<console>/ws?ticket=... A ticket is an API call, and the
 *     authorizer counts every API call as activity - so a ticket is asked for
 *     ONLY while the person is active (isActive). Otherwise the channel waits,
 *     and the next activity() connects. The socket never keeps an idle
 *     session alive.
 *   - Once open: onConnected() (the provider reads the unread count once -
 *     anything pushed while the socket was down is missed), and a ping every
 *     5 minutes so API Gateway's 10-minute idle timer does not close it.
 *     Messages are pushed to onPush; nothing else is sent.
 *   - Closed - by API Gateway's 2-hour limit, the server's 15-minute recheck,
 *     a network blip: reconnect after 2 s, 4 s, 8 s ... up to 5 minutes, each
 *     attempt only while active.
 *   - While there is no socket: a fallback tick every heartbeat (a minute),
 *     only while active - the provider reads the unread count, so the badge is
 *     at most a minute stale when the socket cannot be had (blocked by a
 *     proxy, /ws not routed yet, the local dev server).
 *   - stop() closes everything: sign-out, a session that ended.
 */

/** API Gateway closes a socket idle for 10 minutes; a ping every 5 keeps it. */
export const PING_MS = 5 * 60_000;
/** The first reconnect waits this long, doubling to MAX_BACKOFF_MS. */
export const FIRST_BACKOFF_MS = 2_000;
export const MAX_BACKOFF_MS = 5 * 60_000;
/** The fallback read's cadence while there is no socket. */
export const FALLBACK_MS = 60_000;
/**
 * A socket that has not opened by now is given up on and retried (with the
 * backoff). An upgrade nobody answers - a proxy that swallows it, a path
 * nothing serves - otherwise leaves the socket CONNECTING for good: never
 * open, never closed, so neither the retries nor the fallback read would run.
 */
export const OPEN_TIMEOUT_MS = 10_000;

/**
 * @param {object} deps
 * @param {() => Promise<{ ticket: string }>} deps.requestTicket     POST notifications/socket-ticket
 * @param {(ticket: string) => { send, close, onopen, onmessage, onclose, onerror }} deps.openSocket
 * @param {(message: object) => void} deps.onPush                    a parsed push
 * @param {(info: { downMs: number|null }) => void} [deps.onConnected]  the socket opened; downMs is how long
 *                                                                  there was no socket before it (null on the
 *                                                                  first open) - the live hub uses it to tell a
 *                                                                  brief reconnect from an outage
 * @param {() => void} [deps.onFallbackTick]                        read the count the slow way
 * @param {() => boolean} deps.isActive                             businessDay.js pollGate's verdict
 * @param {(state: string) => void} [deps.onState]                  'CONNECTING' | 'OPEN' | 'WAITING' | 'STOPPED'
 */
export function createNotificationChannel({
  requestTicket, openSocket, onPush, onConnected = () => {}, onFallbackTick = () => {}, isActive, onState = () => {},
  setTimer = setTimeout, clearTimer = clearTimeout, setRepeat = setInterval, clearRepeat = clearInterval,
}) {
  let socket = null;
  let stopped = true;
  let connecting = false;
  let failures = 0;
  let reconnectTimer = null;
  let pingTimer = null;
  let fallbackTimer = null;
  let openTimer = null;
  // When the last open socket went away (null before the first open): onConnected's downMs.
  let lostAt = null;
  const now = () => Date.now();

  const state = (s) => onState(s);

  function startFallback() {
    if (fallbackTimer !== null) return;
    fallbackTimer = setRepeat(() => {
      if (!stopped && socket === null && isActive()) onFallbackTick();
    }, FALLBACK_MS);
  }

  function stopFallback() {
    if (fallbackTimer !== null) clearRepeat(fallbackTimer);
    fallbackTimer = null;
  }

  function scheduleReconnect() {
    if (stopped || reconnectTimer !== null) return;
    const wait = Math.min(MAX_BACKOFF_MS, FIRST_BACKOFF_MS * 2 ** Math.min(failures, 16));
    failures += 1;
    reconnectTimer = setTimer(() => {
      reconnectTimer = null;
      connect();
    }, wait);
  }

  async function connect() {
    if (stopped || socket !== null || connecting) return;
    // A ticket is an API call - activity - so never while the person is away.
    if (!isActive()) {
      state('WAITING');
      return;
    }
    connecting = true;
    state('CONNECTING');
    let ticket;
    try {
      ({ ticket } = await requestTicket());
    } catch {
      connecting = false;
      state('WAITING');
      scheduleReconnect();
      return;
    }
    if (stopped) {
      connecting = false;
      return;
    }
    let ws;
    try {
      ws = openSocket(ticket);
    } catch {
      connecting = false;
      scheduleReconnect();
      return;
    }
    connecting = false;
    socket = ws;
    // An upgrade that is never answered: give up on it after OPEN_TIMEOUT_MS, as a failure.
    openTimer = setTimer(() => {
      openTimer = null;
      if (socket !== ws) return;
      try {
        ws.close();
      } catch {
        // Never opened: nothing to close.
      }
      closed();
    }, OPEN_TIMEOUT_MS);
    ws.onopen = () => {
      if (openTimer !== null) clearTimer(openTimer);
      openTimer = null;
      failures = 0;
      stopFallback();
      state('OPEN');
      pingTimer = setRepeat(() => {
        try {
          ws.send(JSON.stringify({ action: 'ping' }));
        } catch {
          // A send on a closing socket: onclose follows.
        }
      }, PING_MS);
      onConnected({ downMs: lostAt === null ? null : Math.max(0, now() - lostAt) });
    };
    ws.onmessage = (event) => {
      let message = null;
      try {
        message = JSON.parse(event.data);
      } catch {
        return;
      }
      if (message && typeof message === 'object') onPush(message);
    };
    function closed() {
      if (socket !== ws) return;
      socket = null;
      if (openTimer !== null) clearTimer(openTimer);
      openTimer = null;
      // Only a socket that had opened is "lost" (it is the one with a ping timer):
      // a failed attempt to reconnect keeps the time the open one went away.
      if (pingTimer !== null) lostAt = now();
      if (pingTimer !== null) clearRepeat(pingTimer);
      pingTimer = null;
      if (stopped) return;
      state('WAITING');
      startFallback();
      scheduleReconnect();
    }
    ws.onclose = closed;
    ws.onerror = () => {};
  }

  return {
    /** Start: connect now if the person is active, else wait for activity(). The fallback runs until a socket opens. */
    start() {
      if (!stopped) return;
      stopped = false;
      failures = 0;
      startFallback();
      connect();
    },
    /** The person did something: connect at once if waiting with no reconnect pending. */
    activity() {
      if (!stopped && socket === null && !connecting && reconnectTimer === null) connect();
    },
    /** Close everything: sign-out, or a session that ended. */
    stop() {
      stopped = true;
      if (reconnectTimer !== null) clearTimer(reconnectTimer);
      reconnectTimer = null;
      if (pingTimer !== null) clearRepeat(pingTimer);
      pingTimer = null;
      if (openTimer !== null) clearTimer(openTimer);
      openTimer = null;
      stopFallback();
      const ws = socket;
      socket = null;
      if (ws) {
        try {
          ws.close();
        } catch {
          // Already closed.
        }
      }
      state('STOPPED');
    },
    /** Whether a socket is open now (the provider shows "live" or not). */
    get open() {
      return socket !== null;
    },
  };
}
